import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';


const MiPerfil = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para los campos editables
  const [editData, setEditData] = useState({
    document_number: '',
    phone_number: '',
    nombres: '',
    apellidos: ''
  });

  // Obtener el usuario actual y sus datos
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Obtener usuario actual
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        setError('Error al obtener usuario: ' + authError.message);
        return;
      }

      if (!user) {
        setError('No hay usuario autenticado');
        return;
      }

      // Obtener datos del perfil
      const { data, error } = await supabase
        .from('person')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        setError('Error al cargar perfil: ' + error.message);
        return;
      }

      setProfile(data);
      
      // Separar nombres y apellidos del campo full_name
      const [nombres = '', apellidos = ''] = (data.full_name || '').split(';');
      
      setEditData({
        document_number: data.document_number || '',
        phone_number: data.phone_number || '',
        nombres: nombres.trim(),
        apellidos: apellidos.trim()
      });

    } catch (err) {
      setError('Error inesperado: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setEditing(false);
    // Restaurar datos originales
    const [nombres = '', apellidos = ''] = (profile.full_name || '').split(';');
    setEditData({
      document_number: profile.document_number || '',
      phone_number: profile.phone_number || '',
      nombres: nombres.trim(),
      apellidos: apellidos.trim()
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Obtener usuario actual
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('No hay usuario autenticado');
        return;
      }

      // Construir full_name combinando nombres y apellidos
      const fullName = `${editData.nombres.trim()};${editData.apellidos.trim()}`;

      // Actualizar en la base de datos
      const { data, error } = await supabase
        .from('person')
        .update({
          document_number: editData.document_number.trim(),
          phone_number: editData.phone_number.trim(),
          full_name: fullName
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        setError('Error al actualizar perfil: ' + error.message);
        return;
      }

      // Actualizar el estado local
      setProfile(data);
      setEditing(false);
      setSuccess('Perfil actualizado correctamente');

    } catch (err) {
      setError('Error inesperado: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setEditData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={3}>
        <Alert severity="error">No se pudo cargar la información del perfil</Alert>
      </Box>
    );
  }

  // Separar nombres y apellidos para mostrar
  const [nombres = '', apellidos = ''] = (profile.full_name || '').split(';');

  return (
    <Box p={3} maxWidth="800px" mx="auto">
      <Card elevation={3}>
        <CardContent>
          {/* Header con avatar y título */}
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar sx={{ width: 80, height: 80, mr: 3, bgcolor: '#3d475d' }}>
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h4" gutterBottom>
                Mi Perfil
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona tu información personal
              </Typography>
            </Box>
            {!editing && (
              <IconButton
                onClick={handleEdit}
                color="primary"
                size="large"
                sx={{ 
                  bgcolor: '#3d475d', 
                  color: 'white', 
                  '&:hover': { bgcolor: '#2c3441' },
                  '&:active': { bgcolor: '#1e252f' }
                }}
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Mensajes de estado */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Formulario / Vista de datos */}
          <Grid container spacing={3}>
            {/* Nombres */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Nombres"
                value={editing ? editData.nombres : nombres.trim()}
                onChange={handleInputChange('nombres')}
                disabled={!editing}
                variant={editing ? "outlined" : "filled"}
                InputProps={{
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#3d475d',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#3d475d',
                    },
                  },
                }}
              />
            </Grid>

            {/* Apellidos */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Apellidos"
                value={editing ? editData.apellidos : apellidos.trim()}
                onChange={handleInputChange('apellidos')}
                disabled={!editing}
                variant={editing ? "outlined" : "filled"}
                InputProps={{
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#3d475d',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#3d475d',
                    },
                  },
                }}
              />
            </Grid>

            {/* Número de Documento */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Número de Documento"
                value={editing ? editData.document_number : (profile.document_number || 'No registrado')}
                onChange={handleInputChange('document_number')}
                disabled={!editing}
                variant={editing ? "outlined" : "filled"}
                InputProps={{
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#3d475d',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#3d475d',
                    },
                  },
                }}
              />
            </Grid>

            {/* Número de Celular */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Número de Celular"
                value={editing ? editData.phone_number : (profile.phone_number || 'No registrado')}
                onChange={handleInputChange('phone_number')}
                disabled={!editing}
                variant={editing ? "outlined" : "filled"}
                InputProps={{
                  readOnly: !editing,
                }}
              />
            </Grid>

            {/* Email (solo lectura) */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                value={profile.email || 'No registrado'}
                disabled
                variant="filled"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {/* Fecha de Registro (solo lectura) */}
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Fecha de Registro"
                value={formatDate(profile.created_at)}
                disabled
                variant="filled"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>

          {/* Botones de acción */}
          {editing && (
            <Box mt={4}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  startIcon={<CancelIcon />}
                  disabled={saving}
                  sx={{
                    borderColor: '#3d475d',
                    color: '#3d475d',
                    '&:hover': {
                      borderColor: '#2c3441',
                      color: '#2c3441',
                      bgcolor: 'rgba(61, 71, 93, 0.04)'
                    }
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={saving}
                  sx={{ 
                    minWidth: 120,
                    bgcolor: '#3d475d',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#2c3441'
                    },
                    '&:active': {
                      bgcolor: '#1e252f'
                    },
                    '&:disabled': {
                      bgcolor: '#8a94a8',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MiPerfil;